import EMOJI_REGEX from '../../lib/twemojiRegex';
import { IS_EMOJI_SUPPORTED } from '../browser/windowEnvironment';
import {
  nativeToUnifiedExtendedWithCache,
} from '../emoji/emoji';
import fixNonStandardEmoji from '../emoji/fixNonStandardEmoji';

/**
 * Lightweight incremental markdown-to-DOM renderer for streaming agent responses.
 * Replaces the `streaming-markdown` npm package (~12KB min) with ~150 lines.
 *
 * Supports the subset of markdown that AI agents actually produce:
 * - Paragraphs, line breaks
 * - Bold (**), italic (*), strikethrough (~~), inline code (`)
 * - Code blocks (``` with optional language)
 * - Headings (#..####)
 * - Unordered/ordered lists
 * - Blockquotes (>)
 * - Links [text](url)
 * - Horizontal rules (---)
 *
 * This renderer is ephemeral — the streaming DOM is replaced by the entity-based
 * final render once the full message arrives from teamgram with entities.
 * Visual fidelity is good-enough, not pixel-perfect.
 */

export interface StreamParser {
  /** Append a chunk of markdown text (incremental). */
  write(chunk: string): void;
  /** Flush remaining state and close the parser. */
  end(): void;
}

const BLOCK_RE = /^(#{1,6})\s|^>\s?|^```|^---+\s*$|^(\s*)([-*]|\d+\.)\s|^\|/;
const HEADING_RE = /^(#{1,6})\s+(.+)$/;
const HEADING_SOFT_WRAP_CONTINUATION_RE = /^[a-z0-9("'[\u2018\u2019\u201c\u201d]/;

function mergeSoftWrappedHeadings(text: string): string {
  const lines = text.split('\n');
  const merged: string[] = [];

  for (let index = 0; index < lines.length; index++) {
    let currentLine = lines[index];

    if (!HEADING_RE.test(currentLine)) {
      merged.push(currentLine);
      continue;
    }

    while (index + 1 < lines.length) {
      const nextLine = lines[index + 1];
      const trimmedNextLine = nextLine.trim();

      if (!trimmedNextLine) break;
      if (BLOCK_RE.test(nextLine)) break;
      if (!HEADING_SOFT_WRAP_CONTINUATION_RE.test(trimmedNextLine)) break;

      currentLine += ` ${trimmedNextLine}`;
      index++;
    }

    merged.push(currentLine);
  }

  return merged.join('\n');
}

export function normalizeStreamingMarkdown(text: string): string {
  // 1. Fix bold markers split across lines:
  //    "text**\n" or "\n**" as orphan lines → merge into adjacent line
  let result = text
    // Closing ** on its own line → merge with previous line
    .replace(/\n\*\*\s*\n/g, '**\n\n')
    // Opening ** on its own line followed by text → merge with next line
    .replace(/\n\*\*\n([^\n*])/g, '\n**$1')
    // Trailing \n** at end → merge with previous content
    .replace(/([^\n])\n\*\*$/g, '$1**');

  // 2. Unwrap tables from code fences: ```\n| ... |\n``` → just the table lines
  result = result.replace(/```\n?((?:\|[^\n]*\n?)+)```/g, '$1');

  // 3. Merge broken table header rows:
  //    | col1 | col2 |\n col3 | col4 |\n|---|---| → | col1 | col2 | col3 | col4 |\n|---|---|
  result = result.replace(
    /(\|[^\n]+\|)\n([^|\n][^\n]*\|)/g,
    '$1 $2',
  );

  // 4. Repair soft wraps inside headings such as:
  //    ## What I would\n do next
  result = mergeSoftWrappedHeadings(result);

  return result;
}

export function hasBlockMarkdown(text: string): boolean {
  return /(^|\n)(#{1,6}\s|>\s?|---+\s*$|(\s*)([-*]|\d+\.)\s)/m.test(text);
}

export function renderMarkdownToHtml(text: string): string {
  const root = document.createElement('div');
  const parser = createStreamParser(root);
  parser.write(normalizeStreamingMarkdown(text));
  parser.end();
  return root.innerHTML;
}

export function createStreamParser(root: HTMLElement): StreamParser {
  let buffer = '';
  let currentBlock: HTMLElement | undefined;
  let inCodeFence = false;
  let codeLang = '';
  let codeContent = '';
  let ended = false;

  function flushLine(line: string) {
    if (inCodeFence) {
      if (line.match(/^```\s*$/)) {
        inCodeFence = false;
        // If code content is a table (all non-empty lines start with |), render as table
        const codeLines = codeContent.split('\n').filter((l) => l.trim());
        const isTable = codeLines.length > 0 && codeLines.every((l) => l.trimStart().startsWith('|'));
        if (isTable) {
          // Render as table — feed each line through the table handler
          codeLines.forEach((tl) => flushLine(tl));
          closeParagraph();
        } else {
          const pre = document.createElement('pre');
          const code = document.createElement('code');
          if (codeLang) code.className = `language-${codeLang}`;
          code.textContent = codeContent;
          pre.appendChild(code);
          root.appendChild(pre);
        }
        codeContent = '';
        codeLang = '';
        currentBlock = undefined;
        return;
      }
      codeContent += (codeContent ? '\n' : '') + line;
      return;
    }

    // Code fence open
    const fenceMatch = line.match(/^```(\w*)\s*$/);
    if (fenceMatch) {
      closeParagraph();
      inCodeFence = true;
      codeLang = fenceMatch[1] || '';
      codeContent = '';
      return;
    }

    // Horizontal rule
    if (/^---+\s*$/.test(line)) {
      closeParagraph();
      root.appendChild(document.createElement('hr'));
      return;
    }

    // Heading
    const headingMatch = line.match(HEADING_RE);
    if (headingMatch) {
      closeParagraph();
      const level = Math.min(headingMatch[1].length, 4);
      const h = document.createElement(`h${level}`);
      appendInline(h, headingMatch[2]);
      root.appendChild(h);
      return;
    }

    // Blockquote
    const bqMatch = line.match(/^>\s?(.*)$/);
    if (bqMatch) {
      closeParagraph();
      const bq = document.createElement('blockquote');
      appendInline(bq, bqMatch[1]);
      root.appendChild(bq);
      return;
    }

    // Table row: | col | col |
    if (line.trimStart().startsWith('|')) {
      const trimmed = line.trim();
      // Skip separator rows like |---|---|
      if (/^\|[\s:]*-+[\s:]*\|/.test(trimmed)) return;

      const cells = trimmed.split('|').filter(Boolean).map((c) => c.trim());
      if (cells.length > 0) {
        if (!currentBlock || currentBlock.tagName !== 'TABLE') {
          closeParagraph();
          currentBlock = document.createElement('table');
          root.appendChild(currentBlock);
        }
        const tr = document.createElement('tr');
        const isHeader = !currentBlock.querySelector('tr');
        cells.forEach((cell) => {
          const td = document.createElement(isHeader ? 'th' : 'td');
          appendInline(td, cell);
          tr.appendChild(td);
        });
        currentBlock.appendChild(tr);
        return;
      }
    }

    // Table row continuation: building a table and line has pipes but doesn't start with |
    if (currentBlock?.tagName === 'TABLE' && !line.trimStart().startsWith('|')
      && line.includes('|') && line.trim().endsWith('|')) {
      const lastTr = currentBlock.lastElementChild as HTMLElement;
      if (lastTr) {
        const cells = line.trim().split('|').filter(Boolean).map((c) => c.trim());
        const isHeader = lastTr.children[0]?.tagName === 'TH';
        cells.forEach((cell) => {
          const td = document.createElement(isHeader ? 'th' : 'td');
          appendInline(td, cell);
          lastTr.appendChild(td);
        });
        return;
      }
    }

    // Unordered list item
    const ulMatch = line.match(/^(\s*)[-*]\s+(.+)$/);
    if (ulMatch) {
      ensureList('ul');
      const li = document.createElement('li');
      appendInline(li, ulMatch[2]);
      currentBlock!.appendChild(li);
      return;
    }

    // Ordered list item
    const olMatch = line.match(/^(\s*)\d+\.\s+(.+)$/);
    if (olMatch) {
      ensureList('ol');
      const li = document.createElement('li');
      appendInline(li, olMatch[2]);
      currentBlock!.appendChild(li);
      return;
    }

    // Blank line — close current paragraph
    if (line.trim() === '') {
      closeParagraph();
      return;
    }

    // Regular text — append to paragraph
    if (!currentBlock || currentBlock.tagName !== 'P') {
      closeParagraph();
      currentBlock = document.createElement('p');
      root.appendChild(currentBlock);
    } else {
      currentBlock.appendChild(document.createElement('br'));
    }
    appendInline(currentBlock, line);
  }

  function ensureList(tag: 'ul' | 'ol') {
    if (currentBlock && currentBlock.tagName === tag.toUpperCase()) return;
    closeParagraph();
    currentBlock = document.createElement(tag);
    root.appendChild(currentBlock);
  }

  function closeParagraph() {
    currentBlock = undefined;
  }

  function appendText(parent: HTMLElement, text: string) {
    if (!text) return;

    if (IS_EMOJI_SUPPORTED) {
      parent.appendChild(document.createTextNode(text));
      return;
    }

    const normalizedText = fixNonStandardEmoji(text);

    EMOJI_REGEX.lastIndex = 0;
    const parts = normalizedText.split(EMOJI_REGEX);
    EMOJI_REGEX.lastIndex = 0;
    const emojis = normalizedText.match(EMOJI_REGEX) || [];

    if (!emojis.length) {
      parent.appendChild(document.createTextNode(normalizedText));
      return;
    }

    parent.appendChild(document.createTextNode(parts[0] || ''));

    emojis.forEach((emoji, index) => {
      const code = nativeToUnifiedExtendedWithCache(emoji);
      if (!code) {
        parent.appendChild(document.createTextNode(emoji));
      } else {
        const src = `./img-apple-64/${code}.png`;
        const img = document.createElement('img');

        img.setAttribute('src', src);
        img.setAttribute('alt', emoji);
        img.setAttribute('data-path', src);
        img.setAttribute('draggable', 'false');
        img.className = 'emoji emoji-small';

        parent.appendChild(img);
      }

      const tail = parts[index * 2 + 2];
      if (tail) {
        parent.appendChild(document.createTextNode(tail));
      }
    });
  }

  /** Parse inline markdown and append to parent element. */
  function appendInline(parent: HTMLElement, text: string) {
    // Process inline patterns: **bold**, *italic*, ~~strike~~, `code`, [text](url)
    let i = 0;
    let plain = '';

    const flush = () => {
      if (plain) {
        appendText(parent, plain);
        plain = '';
      }
    };

    while (i < text.length) {
      // Bold: **text**
      if (text[i] === '*' && text[i + 1] === '*') {
        const close = text.indexOf('**', i + 2);
        if (close > i + 2) {
          flush();
          const strong = document.createElement('strong');
          appendText(strong, text.slice(i + 2, close));
          parent.appendChild(strong);
          i = close + 2;
          continue;
        }
      }

      // Strikethrough: ~~text~~
      if (text[i] === '~' && text[i + 1] === '~') {
        const close = text.indexOf('~~', i + 2);
        if (close > i + 2) {
          flush();
          const del = document.createElement('del');
          appendText(del, text.slice(i + 2, close));
          parent.appendChild(del);
          i = close + 2;
          continue;
        }
      }

      // Inline code: `text`
      if (text[i] === '`' && text[i + 1] !== '`') {
        const close = text.indexOf('`', i + 1);
        if (close > i + 1) {
          flush();
          const code = document.createElement('code');
          code.textContent = text.slice(i + 1, close);
          parent.appendChild(code);
          i = close + 1;
          continue;
        }
      }

      // Link: [text](url)
      if (text[i] === '[') {
        const closeBracket = text.indexOf(']', i + 1);
        if (closeBracket > i + 1 && text[closeBracket + 1] === '(') {
          const closeParen = text.indexOf(')', closeBracket + 2);
          if (closeParen > closeBracket + 2) {
            flush();
            const a = document.createElement('a');
            a.href = text.slice(closeBracket + 2, closeParen);
            appendText(a, text.slice(i + 1, closeBracket));
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            parent.appendChild(a);
            i = closeParen + 1;
            continue;
          }
        }
      }

      // Italic: *text* (single, after bold check)
      if (text[i] === '*' && text[i + 1] !== '*') {
        const close = text.indexOf('*', i + 1);
        if (close > i + 1 && text[close + 1] !== '*') {
          const inner = text.slice(i + 1, close);
          if (inner.trim() && inner[0] !== ' ' && inner[inner.length - 1] !== ' ') {
            flush();
            const em = document.createElement('em');
            appendText(em, inner);
            parent.appendChild(em);
            i = close + 1;
            continue;
          }
        }
      }

      plain += text[i];
      i++;
    }
    flush();
  }

  function processBuffer() {
    // Process complete lines from buffer
    let nlIdx = buffer.indexOf('\n');
    while (nlIdx >= 0) {
      const line = buffer.slice(0, nlIdx);
      buffer = buffer.slice(nlIdx + 1);
      flushLine(line);
      nlIdx = buffer.indexOf('\n');
    }
  }

  return {
    write(chunk: string) {
      if (ended) return;
      buffer += chunk;
      processBuffer();
    },
    end() {
      if (ended) return;
      ended = true;
      // Flush remaining buffer as final line
      if (buffer) {
        flushLine(buffer);
        buffer = '';
      }
      // Close unclosed code fence
      if (inCodeFence) {
        const pre = document.createElement('pre');
        const code = document.createElement('code');
        if (codeLang) code.className = `language-${codeLang}`;
        code.textContent = codeContent;
        pre.appendChild(code);
        root.appendChild(pre);
        inCodeFence = false;
      }
    },
  };
}
